import { motion } from 'framer-motion';
import { MessageSquare, Video, ThumbsUp, ArrowRight } from 'lucide-react';

// TODO: Connect to Supabase swing_analysis_runs or coach_feedback table
interface FeedbackItem {
  coach: string;
  video: string;
  message: string;
  timestamp: string;
  hasVideo: boolean;
}

interface CoachFeedbackTileProps {
  feedbackItems?: FeedbackItem[];
  onFeedbackClick?: (item: FeedbackItem) => void;
  onAcknowledge?: (item: FeedbackItem) => void;
  onViewAll?: () => void;
}

/**
 * Coach Feedback Tile - Recent coaching insights and AI analysis
 * From FIGMA Clubhouse 2.0 design
 * 
 * @TODO Wire to useQuery for recent AI analysis from swing_analysis_runs
 */
export function CoachFeedbackTile({
  feedbackItems,
  onFeedbackClick,
  onAcknowledge,
  onViewAll,
}: CoachFeedbackTileProps) {
  // Mock data - replace with real feedback when connected to Supabase
  const defaultFeedback: FeedbackItem[] = [
    {
      coach: 'Coach Ryan',
      video: 'Driver Swing - Side View',
      message: 'Great hip rotation! Focus on maintaining spine angle through impact.',
      timestamp: '2h ago',
      hasVideo: true,
    },
    {
      coach: 'AI Analysis',
      video: 'Putting Stroke Analysis',
      message: 'Consistent tempo detected. Path deviation: -2° (improving)',
      timestamp: '1d ago',
      hasVideo: false,
    },
  ];

  const feedback = feedbackItems || defaultFeedback;

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)' }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl shadow-md overflow-hidden h-full"
      data-figma-id="coach-feedback-tile"
    >
      <div className="bg-gradient-to-br from-amber-500 to-amber-600 px-6 py-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white">Coach Feedback</h3>
          <MessageSquare className="w-5 h-5 text-amber-100" />
        </div>
        <p className="text-amber-50 text-sm">Recent insights & analysis</p>
      </div>

      <div className="p-6 space-y-4">
        {feedback.map((item, index) => (
          <motion.div
            key={`${item.coach}-${index}`}
            whileHover={{ scale: 1.02 }}
            onClick={() => onFeedbackClick?.(item)}
            className="border border-slate-200 rounded-xl p-4 cursor-pointer transition-all hover:border-amber-300 hover:shadow-sm"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">{item.coach.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-slate-900">{item.coach}</h4>
                  <span className="text-xs text-slate-500">{item.timestamp}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  {item.hasVideo && <Video className="w-3.5 h-3.5" />}
                  <span className="truncate">{item.video}</span>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-sm leading-relaxed mb-3">
              {item.message}
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcknowledge?.(item);
                }}
                className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-1"
              >
                <ThumbsUp className="w-3 h-3" />
                <span>Acknowledge</span>
              </button>
              <button className="text-xs px-3 py-1.5 text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <span>View Details</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        ))}

        <button
          onClick={onViewAll}
          className="w-full mt-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <span>All Feedback</span>
        </button>
      </div>
    </motion.div>
  );
}

